import { WorkInstructionControlRoomDto } from '@operations/app/api'
import { useTranslate } from '@tolgee/react'
import { TomTableActionsProps } from '@tom-ui/ui'
import { MRT_Row } from 'material-react-table'
import { appStore } from 'modules/utils/src/stores/AppStore'
import { useCallback } from 'react'
import {
  canStartWorkInstruction,
  canStopWorkInstruction,
} from '../helpers/work-instruction-table.helper'
import { WorkInstructionViewModel } from '../models/work-instruction-view.model'
import { WorkInstructionUIStore } from '../stores/work-instruction.ui-store'

export const useWorkInstructionTableActions = (uiStore: WorkInstructionUIStore) => {
  const { t } = useTranslate()

  const setPriority = useCallback(
    async (workInstructions: WorkInstructionControlRoomDto[], priority: boolean) => {
      try {
        await uiStore.setPriority(
          workInstructions.filter(wi => wi.isPriority !== priority).map(wi => wi.id),
          priority,
        )
        appStore.setShowAlert(
          'success',
          t(
            priority ? 'prioritySetToHigh' : 'prioritySetToDefault',
            priority ? 'Priority set to high' : 'Priority set to default',
          ),
        )
      } catch (error) {
        appStore.setShowAlert(
          'error',
          t(
            priority ? 'errorSettingHighPriority' : 'errorSettingDefaultPriority',
            priority ? 'Error setting high priority' : 'Error setting default priority',
          ),
        )
      }
    },
    [t, uiStore],
  )

  const handleSetHighPriority = useCallback(
    async (workInstructions: WorkInstructionControlRoomDto[]) =>
      setPriority(workInstructions, true),
    [setPriority],
  )

  const handleSetDefaultPriority = useCallback(
    async (workInstructions: WorkInstructionControlRoomDto[]) =>
      setPriority(workInstructions, false),
    [setPriority],
  )

  const handleStop = useCallback(
    async (workInstructions: WorkInstructionControlRoomDto[]) => {
      try {
        await uiStore.stopWorkInstructions(workInstructions)
        appStore.setShowAlert('success', t('workInstructionStopped', 'Work instruction stopped'))
      } catch (error) {
        appStore.setShowAlert(
          'error',
          t('errorStoppingWorkInstruction', 'Error stopping work instruction'),
        )
      }
    },
    [t, uiStore],
  )

  const handleStart = useCallback(
    async (workInstructions: WorkInstructionControlRoomDto[]) => {
      try {
        await uiStore.startWorkInstruction(workInstructions)
        appStore.setShowAlert('success', t('workInstructionStarted', 'Work instruction started'))
      } catch (error) {
        appStore.setShowAlert(
          'error',
          t('errorStartingWorkInstruction', 'Error starting work instruction'),
        )
      }
    },
    [t, uiStore],
  )

  return useCallback(
    (
      selectedRows: MRT_Row<WorkInstructionViewModel>[],
      canStartOrStop: boolean,
    ): TomTableActionsProps<WorkInstructionViewModel>[] => {
      const workInstructions = selectedRows.map(row => row.original)
      const actions: TomTableActionsProps<WorkInstructionViewModel>[] = []

      if (canStartOrStop) {
        actions.push(
          {
            label: t('start', 'Start'),
            grouped: true,
            disabled: workInstructions.some(wi => !canStartWorkInstruction(wi.status)),
            onClick: () => handleStart(workInstructions),
          },
          {
            label: t('stop', 'Stop'),
            grouped: true,
            disabled: workInstructions.some(wi => !canStopWorkInstruction(wi.status)),
            onClick: () => handleStop(workInstructions),
          },
        )
      }

      return [
        ...actions,
        {
          label: t('setHighPriority', 'Set High Priority'),
          grouped: true,
          disabled: workInstructions.every(wi => wi.isPriority),
          onClick: () => handleSetHighPriority(workInstructions),
        },
        {
          label: t('setDefaultPriority', 'Set Default Priority'),
          grouped: true,
          disabled: workInstructions.every(wi => !wi.isPriority),
          onClick: () => handleSetDefaultPriority(workInstructions),
        },
      ]
    },
    [handleSetDefaultPriority, handleSetHighPriority, handleStart, handleStop, t],
  )
}
